import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
} from '@angular/core';
import { TelXLButtonComponent, TelXLPanelComponent } from '@dxp/elements';
import { IFileEntry } from '@dxp/core/models';
import { mediaIcon } from '@dxp/core/helpers';
import { TelXLContentPreviewService } from '../../content-preview/services/content-preview.service';

@Component({
    selector: 'telxl-shared-file',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TelXLPanelComponent, TelXLButtonComponent],
    templateUrl: './file-panel.component.html',
    styleUrl: './file-panel.component.scss',
})
export class TelXLFilePanelComponent {
    readonly #previewService = inject(TelXLContentPreviewService);

    file = input.required<IFileEntry>();
    delete = output<IFileEntry>();

    size = computed<string>(() => {
        const bytes = this.file()?.file?.size ?? 0;
        const mb = bytes / (1024 * 1024);
        const kb = bytes / 1024;

        return mb >= 1
            ? `${Math.round(mb)}mb`
            : kb >= 1
              ? `${Math.round(kb)}kb`
              : `${Math.round(bytes)}b`;
    });

    cardClass = computed<string>(() => {
        const classes = ['mt-2'];

        if (this.file().state === 'failed') classes.push('border-danger');
        else if (
            this.file().state === 'duplicate' ||
            this.file().state === 'toolarge'
        )
            classes.push('border-warning');
        else if (this.file().state === 'unsupported')
            classes.push('background');

        return classes.join(' ');
    });

    fileType = computed<string>(() => {
        return mediaIcon(this.file().file.name);
    });

    canPreview = computed<boolean>(() =>
        this.#previewService.canPreview(
            this.file().file.type,
            this.file().file.name,
        ),
    );

    preview() {
        if (!this.canPreview()) return;

        this.#previewService.open({
            url: this.file().url ?? '',
            contentType: this.file().file.type,
            fileName: this.file().file.name,
        });
    }

    remove() {
        this.delete.emit(this.file());
    }
}
